import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
  ReferenceDot,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { 
  ArrowRight, 
  TrendingUp,
  Activity,
  Target,
  Shield,
  Server,
  CheckCircle,
  Play,
  Euro,
  BarChart3,
  Gauge
} from "lucide-react";

const ValueDashboard = () => {
  const { currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [activeMonth, setActiveMonth] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // ROI Timeline Data - 36 Months
  const roiData = [];
  let cumulativeInvestment = 0;
  let cumulativeValue = 0;
  
  for (let i = 0; i <= 36; i++) {
    // Investment: Higher in early months (setup), then steady OPEX
    const monthlyInvestment = i <= 3 ? 180000 : i <= 6 ? 120000 : 80000;
    cumulativeInvestment += monthlyInvestment;
    
    // Value: Starts slow, accelerates after month 6
    const monthlyValue = i <= 3 ? 20000 : i <= 6 ? 80000 : i <= 12 ? 150000 : i <= 24 ? 200000 : 250000;
    cumulativeValue += monthlyValue;
    
    roiData.push({
      month: i,
      investment: Math.round(cumulativeInvestment / 1000), // in Tsd. €
      value: Math.round(cumulativeValue / 1000), // in Tsd. €
      investmentExact: cumulativeInvestment,
      valueExact: cumulativeValue
    });
  }

  // Find Break-Even Point
  const breakEvenMonth = roiData.find(d => d.value >= d.investment)?.month || 12;
  const breakEvenData = roiData[breakEvenMonth];

  // Project Portfolio Data for Radar Chart
  const projectData = [
    { project: 'Projekt A', impact: 85, feasibility: 90, risk: 25, progress: 70 },
    { project: 'Projekt B', impact: 70, feasibility: 75, risk: 40, progress: 45 },
    { project: 'Projekt C', impact: 95, feasibility: 60, risk: 55, progress: 30 },
  ];

  const radarData = [
    { dimension: 'Business Impact', A: 85, B: 70, C: 95 },
    { dimension: 'Machbarkeit', A: 90, B: 75, C: 60 },
    { dimension: 'Risikolevel', A: 75, B: 60, C: 45 },
    { dimension: 'Fortschritt', A: 70, B: 45, C: 30 },
  ];

  // KPI Cards Data
  const kpiCards = [
    {
      label: 'Realized EBIT Impact',
      value: '€3.5M',
      trend: '+12%',
      trendUp: true,
      icon: Euro
    },
    {
      label: 'AI ROI Factor',
      value: '5.1x',
      trend: '+0.8x',
      trendUp: true,
      icon: TrendingUp
    },
    {
      label: 'Active Initiatives',
      value: '23',
      trend: '4 neue',
      trendUp: true,
      icon: Activity
    }
  ];

  const seoData = {
    title: 'Value-Dashboard | CFO-Cockpit für KI-Performance - BROVKO',
    description: 'Echtzeit-Transparenz über EBIT-Impact, ROI und Projektreifegrad Ihres KI-Portfolios. Das CFO-Cockpit der KI-Value-Engine.',
    keywords: 'KI Dashboard, CFO Cockpit, EBIT Impact, AI ROI, KI Portfolio Management',
    canonicalUrl: '/value-dashboard'
  };

  // Custom Tooltip for ROI Chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const investmentValue = payload.find(p => p.dataKey === 'investment')?.payload?.investmentExact || 0;
      const generatedValue = payload.find(p => p.dataKey === 'value')?.payload?.valueExact || 0;
      const netValue = generatedValue - investmentValue;
      
      return (
        <div className="theme-brand bg-surface text-ink bg-surface-raised border border-hairline-strong rounded-xl p-4 shadow-2xl min-w-[220px]">
          <p className="text-white font-semibold mb-3 pb-2 border-b border-hairline">
            Monat {label}
          </p>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-ink-faint text-sm">Investment:</span>
              <span className="text-red-400 font-mono font-semibold">
                €{investmentValue.toLocaleString('de-DE')}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-ink-faint text-sm">Generierter Wert:</span>
              <span className="text-brand-400 font-mono font-semibold">
                €{generatedValue.toLocaleString('de-DE')}
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-hairline">
              <span className="text-ink-muted text-sm font-medium">Netto:</span>
              <span className={`font-mono font-bold ${netValue >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {netValue >= 0 ? '+' : ''}€{netValue.toLocaleString('de-DE')}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const [currentDate] = useState(() => new Date().toLocaleDateString('de-DE'));
  const [nextAuditDate] = useState(() => new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString('de-DE'));

  return (
    <div className="min-h-screen bg-surface">
      <SEOHead {...seoData} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 pb-6">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-3">
              Value-Dashboard: Ihr <span className="bg-brand-accent bg-clip-text text-transparent">CFO-Cockpit</span> für KI-Performance
            </h1>
            
            <p className="text-base text-ink-faint max-w-[62ch]">
              Echtzeit-Transparenz über EBIT-Impact, ROI und Projektreifegrad Ihres KI-Portfolios.
            </p>
          </div>

          {/* KPI Cards */}
          <div className="grid md:grid-cols-3 gap-3 mb-8">
            {kpiCards.map((kpi, index) => (
              <div 
                key={index}
                className={`bg-surface-raised/50 backdrop-blur border border-hairline rounded-lg p-4 transition-all duration-500 hover:border-teal-500/50 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-ink-faint text-xs">{kpi.label}</span>
                  <kpi.icon className="h-4 w-4 text-brand-400" />
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold text-white">{kpi.value}</span>
                  <div className={`flex items-center gap-1 text-xs ${kpi.trendUp ? 'text-green-400' : 'text-red-400'}`}>
                    <TrendingUp className={`h-3 w-3 ${!kpi.trendUp && 'rotate-180'}`} />
                    <span>{kpi.trend}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main ROI Chart Section */}
      <section className="py-6">
        <div className="container mx-auto px-4">
          <div className="bg-surface-raised/40 backdrop-blur border border-hairline rounded-xl p-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-white mb-1">ROI & Cumulative Value Timeline</h2>
                <p className="text-ink-faint text-xs">36-Monats-Projektion • Hover für Details</p>
              </div>
              <div className="flex items-center gap-4 mt-3 md:mt-0">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-500"></div>
                  <span className="text-ink-faint text-xs">Investment</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-brand"></div>
                  <span className="text-ink-faint text-xs">Wert</span>
                </div>
              </div>
            </div>

            {/* Break-Even Highlight */}
            <div className="bg-brand/10 border border-teal-500/30 rounded-lg p-3 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-brand/15 rounded-full flex items-center justify-center flex-shrink-0">
                <Target className="h-5 w-5 text-brand-400" />
              </div>
              <div>
                <p className="text-brand-400 font-semibold text-sm">Break-Even nach {breakEvenMonth} Monaten</p>
                <p className="text-ink-faint text-xs">
                  Ab diesem Punkt übersteigt der generierte Wert die kumulierten Investitionen.
                </p>
              </div>
            </div>

            {/* Interactive Chart */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={100}>
                <AreaChart 
                  data={roiData} 
                  margin={{ top: 15, right: 15, left: 15, bottom: 5 }}
                  onMouseMove={(e) => {
                    if (e && e.activeTooltipIndex !== undefined) {
                      setActiveMonth(e.activeTooltipIndex);
                    }
                  }}
                  onMouseLeave={() => setActiveMonth(null)}
                >
                  <defs>
                    <linearGradient id="colorValueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorInvestGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#64748b" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#64748b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    tickFormatter={(value) => value % 6 === 0 ? `M${value}` : ''}
                  />
                  <YAxis 
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    tickFormatter={(value) => `${value}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  
                  {/* Investment Area */}
                  <Area 
                    type="monotone" 
                    dataKey="investment" 
                    stroke="#64748b" 
                    strokeWidth={2}
                    fill="url(#colorInvestGradient)"
                    name="Investment"
                  />
                  
                  {/* Value Area */}
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#14b8a6" 
                    strokeWidth={3}
                    fill="url(#colorValueGradient)"
                    name="Wert"
                  />
                  
                  {/* Break-Even Reference Line */}
                  <ReferenceLine 
                    x={breakEvenMonth} 
                    stroke="#14b8a6" 
                    strokeDasharray="5 5"
                    strokeWidth={2}
                  />
                  
                  {/* Break-Even Point */}
                  <ReferenceDot 
                    x={breakEvenMonth} 
                    y={breakEvenData?.value || 0} 
                    r={6} 
                    fill="#14b8a6" 
                    stroke="#fff"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Chart Footer */}
            <div className="mt-3 pt-3 border-t border-hairline flex flex-wrap items-center justify-between gap-3">
              <p className="text-ink-faint text-xs">
                * Projektion basiert auf typischen Implementierungsverläufen.
              </p>
              <div className="text-ink-faint text-xs">
                <span className="text-brand-400 font-semibold">+{((roiData[36].value / roiData[36].investment - 1) * 100).toFixed(0)}%</span> ROI nach 36 Monaten
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Section: Portfolio + Sovereignty */}
      <section className="py-6">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-4">
            
            {/* Project Portfolio - Radar Chart (Smaller) */}
            <div className="lg:col-span-2 bg-surface-raised/40 backdrop-blur border border-hairline rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-bold text-white">Projekt-Portfolio Status</h3>
                  <p className="text-ink-faint text-xs">Multi-dimensionale Bewertung</p>
                </div>
                <BarChart3 className="h-4 w-4 text-brand-400" />
              </div>

              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={100}>
                  <RadarChart data={radarData} margin={{ top: 15, right: 25, bottom: 15, left: 25 }}>
                    <PolarGrid stroke="#334155" />
                    <PolarAngleAxis dataKey="dimension" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                    <Radar name="Projekt A" dataKey="A" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.3} strokeWidth={2} />
                    <Radar name="Projekt B" dataKey="B" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} strokeWidth={2} />
                    <Radar name="Projekt C" dataKey="C" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-hairline">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-brand"></div>
                  <span className="text-ink-faint text-xs">Projekt A</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  <span className="text-ink-faint text-xs">Projekt B</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
                  <span className="text-ink-faint text-xs">Projekt C</span>
                </div>
              </div>
            </div>

            {/* Sovereignty & Governance Monitor */}
            <div className="bg-surface-raised/40 backdrop-blur border border-hairline rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Souveränität</h3>
                <Shield className="h-5 w-5 text-brand-400" />
              </div>

              {/* Sovereignty Score */}
              <div className="text-center mb-4">
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-28 h-28">
                    <circle
                      cx="56"
                      cy="56"
                      r="48"
                      fill="none"
                      stroke="#334155"
                      strokeWidth="8"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="48"
                      fill="none"
                      stroke="#14b8a6"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${98 * 3.02} ${100 * 3.02}`}
                      transform="rotate(-90 56 56)"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-white">98%</span>
                    <span className="text-xs text-ink-faint">Datenhoheit</span>
                  </div>
                </div>
              </div>

              {/* Score Explanation - NEW */}
              <div className="bg-slate-700/30 rounded-lg p-3 mb-4 text-xs">
                <p className="text-ink-faint mb-1"><span className="text-brand-400 font-medium">Score basiert auf:</span></p>
                <ul className="space-y-1 text-ink-faint">
                  <li className="flex items-center gap-1.5">
                    <CheckCircle className="h-3 w-3 text-brand-400" />
                    <span>AIWizz Audit Trail (100%)</span>
                  </li>
                  <li className="flex items-center gap-1.5">
                    <CheckCircle className="h-3 w-3 text-brand-400" />
                    <span>DSGVO-Compliance (100%)</span>
                  </li>
                  <li className="flex items-center gap-1.5">
                    <CheckCircle className="h-3 w-3 text-brand-400" />
                    <span>DE-Hosting (98%)</span>
                  </li>
                </ul>
              </div>

              {/* Hosting Badge */}
              <div className="bg-brand/10 border border-teal-500/30 rounded-lg p-3 mb-4 flex items-center gap-3">
                <Server className="h-5 w-5 text-brand-400 flex-shrink-0" />
                <div>
                  <p className="text-brand-400 text-sm font-medium">100% Hosted in Germany</p>
                  <p className="text-ink-faint text-xs">Souveräne Infrastruktur</p>
                </div>
              </div>

              {/* System Health */}
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between py-2 border-b border-hairline">
                  <span className="text-ink-faint">System Health</span>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-medium">OK</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-hairline">
                  <span className="text-ink-faint">Last Update</span>
                  <span className="text-ink-muted">{currentDate}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-ink-faint">Next Audit</span>
                  <span className="text-ink-muted">{nextAuditDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 border border-hairline rounded-xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Play className="h-6 w-6 text-brand-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    Erleben Sie die Engine live in Ihrem Kontext.
                  </h3>
                  <p className="text-ink-faint text-sm">
                    Sehen Sie, wie das Value-Dashboard mit Ihren Daten aussehen würde.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <Link to="/contact">
                  <Button className="w-full sm:w-auto bg-brand hover:bg-brand-600 text-ink-onBrand px-5 py-2 font-semibold text-sm">
                    Live-Demo anfordern
                    <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </Button>
                </Link>
                <Link to="/ki-value-check">
                  <Button variant="outline" className="w-full sm:w-auto border-hairline-strong text-white hover:bg-white/[0.07] px-5 py-2 text-sm">
                    KI-Value Check starten
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ValueDashboard;
